// Global realtime toast surfacing.
//
// Mounted once inside the Toaster (which itself wraps the app shell) — listens
// to org-wide topics and converts a small allowlist of events into toasts
// scoped to the current user's role:
//
//   producer  -> TakeUploaded, BatchSubmitted, PickupRequested
//   supervisor -> BatchSubmitted, CommentCreated (mentioning them)
//   actor     -> PickupRequested (their cue), TakeApproved (theirs)
//
// Anything not in the per-role allowlist is silently dropped — the channel
// invalidation in the per-route hooks already keeps the cache fresh; this
// component is only about surfacing user-visible noise.

import { useToast } from '@ohear/ui'
import { type Role, useAuth } from '../../lib/auth'
import type { RealtimeEvent } from '../../lib/realtime/events'
import { useRealtimeChannel } from '../../lib/realtime/use-channel'
import { useOrgId } from './use-org-id'

/**
 * Per-role allowlist of event types that should pop a toast. The body of the
 * toast is built from the event's `payload` — keep these blurbs short, the
 * notification bell + dedicated pages own the long-form view.
 */
type ToastEventType =
  | 'TakeUploaded'
  | 'TakeApproved'
  | 'BatchSubmitted'
  | 'PickupRequested'
  | 'CommentCreated'

const ROLE_ALLOWLIST: Record<Role, ReadonlySet<ToastEventType>> = {
  owner: new Set<ToastEventType>(['TakeUploaded', 'BatchSubmitted', 'PickupRequested']),
  member: new Set<ToastEventType>(['TakeUploaded', 'BatchSubmitted', 'PickupRequested']),
  supervisor: new Set<ToastEventType>(['BatchSubmitted', 'CommentCreated']),
  actor: new Set<ToastEventType>(['PickupRequested', 'TakeApproved']),
}

interface ToastShape {
  title: string
  description?: string
  severity: 'info' | 'success' | 'warn' | 'error'
}

/**
 * Translate the API's canonical dot-separated event names while retaining
 * compatibility with historical PascalCase rows already in domain_events.
 */
export function toastEventType(event: RealtimeEvent): ToastEventType | null {
  switch (event.event_type) {
    case 'TakeUploaded':
    case 'take.recorded':
      return 'TakeUploaded'
    case 'TakeApproved':
    case 'take.approved':
      return 'TakeApproved'
    case 'BatchSubmitted':
    case 'batch.submitted':
      return 'BatchSubmitted'
    case 'PickupRequested':
    case 'take.rejected_pickup':
      return 'PickupRequested'
    case 'take.rejected':
      return event.payload?.decision === 'rejected_pickup' ? 'PickupRequested' : null
    case 'CommentCreated':
    case 'comment.created':
      return 'CommentCreated'
    default:
      return null
  }
}

function mentionedUserIds(payload: Record<string, unknown>): unknown[] {
  const value = payload.mentioned_user_ids ?? payload.mentions
  return Array.isArray(value) ? value : []
}

export function describeEvent(
  event: RealtimeEvent,
  role: Role,
  currentUserId?: string,
): ToastShape | null {
  const type = toastEventType(event)
  if (!type) return null
  const allowed = ROLE_ALLOWLIST[role]
  if (!allowed.has(type)) return null

  const payload = event.payload ?? {}
  if (role === 'actor' && (type === 'TakeApproved' || type === 'PickupRequested')) {
    // These are organization-wide events. Only surface the toast to its
    // intended actor; _actor_user_id is the reviewer and must not be used.
    const targetUserId = pickString(payload, 'target_user_id')
    if (!currentUserId || targetUserId !== currentUserId) return null
  }
  const character = pickString(payload, 'character_code', 'character_name')
  const cue = pickString(payload, 'cue_number', 'cue_id')
  const project = pickString(payload, 'project_name', 'project_id')

  switch (type) {
    case 'TakeUploaded':
      return {
        severity: 'info',
        title: 'New take uploaded',
        description: joinDesc([character, cue && `cue ${cue}`, project]),
      }
    case 'TakeApproved':
      return {
        severity: 'success',
        title: 'Take approved',
        description: joinDesc([character, cue && `cue ${cue}`]),
      }
    case 'BatchSubmitted':
      return {
        severity: 'info',
        title: 'Batch ready for review',
        description: joinDesc([character, project]),
      }
    case 'PickupRequested':
      return {
        severity: 'warn',
        title: 'Pickup requested',
        description: joinDesc([character, cue && `cue ${cue}`]),
      }
    case 'CommentCreated': {
      // Supervisor-only mention filtering: the event payload sets
      // New events use `mentions`; historical rows may use
      // `mentioned_user_ids`.
      // Untargeted comments are skipped to avoid noise.
      const mentioned = mentionedUserIds(payload)
      if (!currentUserId || !mentioned.includes(currentUserId)) return null
      return {
        severity: 'info',
        title: 'You were mentioned',
        description: joinDesc([character, cue && `cue ${cue}`]),
      }
    }
    default:
      return null
  }
}

function pickString(obj: Record<string, unknown>, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.length > 0) return v
    if (typeof v === 'number') return String(v)
  }
  return null
}

function joinDesc(parts: Array<string | null | false>): string | undefined {
  const filtered = parts.filter((p): p is string => Boolean(p))
  if (filtered.length === 0) return undefined
  return filtered.join(' · ')
}

/**
 * Mount once at app shell level (inside <Toaster>). Subscribes to the four
 * org topics and pushes role-relevant events into the toast queue.
 */
export function RealtimeToastListener() {
  const { role, session } = useAuth()
  const orgId = useOrgId()
  const toast = useToast()

  // Filter helper that toasts when allowed and the event passes the per-role
  // additional filtering (e.g. CommentCreated only when @-mentioned).
  const handle = (event: RealtimeEvent) => {
    if (!role) return
    const desc = describeEvent(event, role, session?.user.id)
    if (!desc) return
    toast.show(desc)
  }

  useRealtimeChannel({ orgId, topic: 'takes', onEvent: handle })
  useRealtimeChannel({ orgId, topic: 'comments', onEvent: handle })
  useRealtimeChannel({ orgId, topic: 'project_progress', onEvent: handle })
  useRealtimeChannel({ orgId, topic: 'notifications', onEvent: handle })

  return null
}
